import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { formatMoney, formatMoment, formatQty } from '@shop/shared'
import { Banner, Empty, ErrorBanner, Spinner } from '@shop/ui'
import { ScanInput, Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { ReturnableLine, SaleHeader, SalesReturnDetail } from '../pos/contracts'

/**
 * المرتجعُ وردُّ المال.
 *
 * ⚠ <b>وقرارُ المالك مرسومٌ في الشاشة لا مخبوءًا في الخادم</b>: من يملك
 *   ترحيلَ المرتجع ولا يملك ردَّ المال <b>يستلم البضاعةَ ويترك المالَ
 *   معلَّقًا</b>. فالزرُّ يقول ذلك صراحةً — «استلِم البضاعةَ (المالُ للمشرف)» —
 *   ولا يُترك الكاشيرُ يضغط ثمّ يُصدَم برفض.
 *
 * <b>وردُّ المال كاملٌ أو مؤجَّل، ولا جزئيّ</b>: لا حقلَ مبلغٍ يُكتب أصلًا،
 * فالمبلغُ هو مجموعُ المرتجع كلِّه.
 */
export default function Returns({
  canRefund,
  onClose,
}: {
  canRefund: boolean
  onClose: () => void
}) {
  const [term, setTerm] = useState('')
  const [chosen, setChosen] = useState<SaleHeader | null>(null)

  // ⚠ **والبحثُ في الخادم بالرقم وبالصنف معًا.** الزبونُ يعود بالبضاعة ولا
  //   يحمل الإيصال — واسمُ الصنف أو باركودُه هو كلُّ ما بيد الكاشير.
  //   والتصفيةُ المحلّيّةُ كانت تبحث في آخر ثلاثين فاتورةً فقط.
  const query = useQuery({
    queryKey: ['sales', 'COMPLETED', term.trim()],
    queryFn: ({ signal }) => pos.listSales('COMPLETED', term, signal),
    placeholderData: (previous) => previous,
  })

  const filtered: SaleHeader[] = query.data ?? []

  if (chosen) {
    return (
      <ReturnBuilder
        sale={chosen}
        canRefund={canRefund}
        onBack={() => setChosen(null)}
        onClose={onClose}
      />
    )
  }

  return (
    <Sheet title="مرتجعُ مبيعات" onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-3">
        <ScanInput
          value={term}
          onChange={setTerm}
          onSubmit={() => filtered.length === 1 && setChosen(filtered[0])}
          placeholder="رقمُ الفاتورة أو اسمُ الصنف أو باركودُه…"
        />

        <ErrorBanner error={query.error} />
        {query.isPending && <Spinner />}

        {!query.isPending && filtered.length === 0 && <Empty>لا فواتيرَ مُتمّة.</Empty>}

        <ul className="grid gap-2">
          {filtered.map((sale) => (
            <li key={sale.id}>
              <button
                className="flex min-h-16 w-full items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-3 text-right"
                type="button"
                onClick={() => setChosen(sale)}
              >
                <span>
                  <span className="tabular block font-bold" dir="ltr">
                    {sale.saleNo}
                  </span>
                  <span className="block text-xs text-ink-soft">
                    {formatMoment(sale.completedAt)}
                    {sale.returnState !== 'NONE' && ' · مُرتجَعٌ جزئيًّا'}
                  </span>
                </span>

                <span className="tabular font-bold" dir="ltr">
                  {formatMoney(sale.total, sale.currency)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Sheet>
  )
}

function ReturnBuilder({
  sale,
  canRefund,
  onBack,
  onClose,
}: {
  sale: SaleHeader
  canRefund: boolean
  onBack: () => void
  onClose: () => void
}) {
  const [picked, setPicked] = useState<Record<string, number>>({})
  const [document, setDocument] = useState<SalesReturnDetail | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // ⚠ **والمتبقّي يُقرأ الآن لا من ذاكرة**: مرتجعٌ آخرُ قد يكون رُحّل قبل
  //   ثوانٍ، وإرجاعُ ما أُرجع يُرفض من القاعدة بعد أن يكون الزبونُ سلّم.
  const query = useQuery({
    queryKey: ['returnable', sale.id],
    queryFn: () => pos.returnable(sale.id),
    staleTime: 0,
  })

  const lines: ReturnableLine[] = (query.data ?? []).filter((row) => row.remainingQty > 0)

  // ⚠ **والمعاينةُ بالسعر بعد الخصم** — وإلّا وعدت الشاشةُ الزبونَ بمبلغٍ
  //   يخالف ما يُرحّله الخادم. والخادمُ هو الحاكم، والشاشةُ تقول ما سيفعله.
  const total = lines.reduce(
    (sum, line) => sum + (picked[line.saleLineId] ?? 0) * line.netUnitPrice,
    0,
  )

  const chosen = Object.entries(picked).filter(([, qty]) => qty > 0)

  const post = async (withMoney: boolean) => {
    setBusy(true)
    setError(null)

    try {
      const draft = await pos.createReturn(sale.id, 'CUSTOMER_RETURN')

      const withLines = await pos.setReturnLines(
        draft.header.id,
        chosen.map(([saleLineId, qty]) => ({ saleLineId, qty })),
      )

      // ⚠ **والمالُ كاملٌ أو لا شيء**: `refunds` فارغةٌ ⇒ مؤجَّل، وممتلئةٌ ⇒
      //   بمجموع المرتجع كلِّه. ولا ثالث.
      const refunds = withMoney
        ? [{ methodCode: 'CASH', amount: withLines.header.total }]
        : []

      setDocument(await pos.postReturn(withLines.header.id, refunds))
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  if (document) {
    return (
      <Sheet title="تمّ المرتجع" onClose={onClose}>
        <div className="mx-auto grid max-w-md gap-4">
          <div className="rounded-xl border border-line bg-surface-raised p-4 text-center">
            <p className="tabular text-2xl font-bold" dir="ltr">
              {document.header.returnNo}
            </p>
            <p className="tabular mt-1 text-lg" dir="ltr">
              {formatMoney(document.header.total, document.header.currency)}
            </p>
          </div>

          {document.header.refundState === 'PENDING' ? (
            <Banner tone="warn">
              <b>البضاعةُ عادت، والمالُ ينتظر المشرف.</b> ويظهر هذا المرتجعُ في
              لوحة «ما ينتظر المشرف» حتى يُردّ.
            </Banner>
          ) : (
            <Banner tone="ok">سُوّي المرتجعُ كاملًا — والمالُ خرج من الورديّة.</Banner>
          )}

          <TouchButton tone="primary" wide onClick={onClose}>
            تمّ
          </TouchButton>
        </div>
      </Sheet>
    )
  }

  return (
    <Sheet title={`مرتجع — ${sale.saleNo}`} onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-3">
        <TouchButton onClick={onBack}>رجوع</TouchButton>

        <ErrorBanner error={error ?? query.error} />
        {query.isPending && <Spinner />}

        {!query.isPending && lines.length === 0 && <Empty>لا شيءَ يبقى قابلًا للإرجاع.</Empty>}

        <ul className="grid gap-2">
          {lines.map((line) => {
            const qty = picked[line.saleLineId] ?? 0

            return (
              <li key={line.saleLineId} className="rounded-xl border border-line bg-surface-raised p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{line.productName}</p>
                    <p className="tabular text-xs text-ink-soft" dir="ltr">
                      {line.discountAmount > 0 && (
                        <s className="me-1 opacity-60">{formatMoney(line.unitPrice)}</s>
                      )}
                      {formatMoney(line.netUnitPrice)} · باقٍ {formatQty(line.remainingQty)}
                    </p>

                    {/* والخصمُ يُقال صراحةً: كاشيرٌ يرى سعرًا أقلَّ من البطاقة
                        بلا سببٍ يظنّه خطأً ويُلغي المرتجع. */}
                    {line.discountAmount > 0 && (
                      <p className="text-xs text-warn">بِيع بخصم — والمردودُ ما دُفع</p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      className="min-h-11 min-w-11 rounded-lg border border-line text-xl"
                      type="button"
                      aria-label="أنقص"
                      onClick={() =>
                        setPicked((current) => ({
                          ...current,
                          [line.saleLineId]: Math.max(qty - 1, 0),
                        }))
                      }
                    >
                      −
                    </button>

                    <span className="tabular min-w-8 text-center font-bold" dir="ltr">
                      {formatQty(qty)}
                    </span>

                    <button
                      className="min-h-11 min-w-11 rounded-lg border border-line text-xl"
                      type="button"
                      aria-label="زِد"
                      onClick={() =>
                        setPicked((current) => ({
                          ...current,
                          [line.saleLineId]: Math.min(qty + 1, line.remainingQty),
                        }))
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>

        {chosen.length > 0 && (
          <>
            <div className="rounded-xl border border-line bg-surface-raised p-3 text-center">
              <div className="text-xs text-ink-soft">قيمةُ المرتجع</div>
              <div className="tabular text-2xl font-bold" dir="ltr">
                {formatMoney(total)}
              </div>
            </div>

            {canRefund ? (
              <TouchButton tone="primary" wide disabled={busy} onClick={() => void post(true)}>
                {busy ? 'يُرحَّل…' : 'استلِم البضاعةَ وردّ المال'}
              </TouchButton>
            ) : (
              <>
                <Banner tone="info">
                  ردُّ المال صلاحيّةٌ مستقلّة. <b>استلِم البضاعةَ الآن</b>، ويردّ
                  المشرفُ المالَ من اللوحة.
                </Banner>

                <TouchButton tone="primary" wide disabled={busy} onClick={() => void post(false)}>
                  {busy ? 'يُرحَّل…' : 'استلِم البضاعة (المالُ للمشرف)'}
                </TouchButton>
              </>
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}
